import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import PricingRow from '../../ui/atoms/PricingRow';

/**
 * PurchasePricingExtras component.
 * Renders a breakdown of additional costs like carbon credits, service fees, insurance, installment fees,
 * and exchange surcharges (e.g. route change, fare difference).
 * @param {object} props - carbon, total, markup, insurance, installmentsFee, serviceLevel, exchangeSurcharges.
 * @param {number} props.carbon - Price of carbon offset credits
 * @param {number} props.total - Total price of all extra charges
 * @param {number} props.markup - Service fee markup amount
 * @param {number} props.insurance - Travel insurance cost
 * @param {number} props.installmentsFee - Fee for installment payment plans
 * @param {object} props.serviceLevel - Selected service level details
 * @param {object} props.exchangeSurcharges - Exchange surcharges breakdown (items with amount, message, type; total)
 * @returns {JSX.Element} Component that displays pricing extras breakdown
 */
const PurchasePricingExtras = ({
  carbon,
  total,
  markup,
  insurance,
  installmentsFee,
  serviceLevel,
  exchangeSurcharges,
}) => {
  const { t } = useTranslation('purchase');
  const { brand } = useWhitelabelEnvs();

  const hasExchangeSurcharges =
    exchangeSurcharges?.total > 0 && Array.isArray(exchangeSurcharges?.items);
  const hasExtras = total > 0 || hasExchangeSurcharges;
  if (!hasExtras) return null;

  return (
    <>
      {markup > 0 && (
        <PricingRow
          label={t('label.service_charge')}
          tooltipContent={t('text.working_for_a_better_shopping_experience')}
          price={markup}
          priceAlignment="flex-end"
        />
      )}

      {serviceLevel.total > 0 && (
        <PricingRow
          label={t(`service-levels.${serviceLevel.name}`, {
            context: 'title',
          })}
          price={serviceLevel.total}
          tooltipContent={t(`tooltip.service_level`, {
            context: serviceLevel?.name,
          })}
          priceAlignment="flex-end"
        />
      )}

      {hasExchangeSurcharges &&
        exchangeSurcharges.items.map((item, index) => (
          <PricingRow
            key={item?.id ?? `exchange-${item?.type ?? 'item'}-${index}`}
            label={item.message || item.type || t('label.exchange_surcharge')}
            price={Number(item.amount) || 0}
            priceAlignment="flex-end"
          />
        ))}

      {total > 0 && (
        <PricingRow label={t('label.additional')} price={total} isMain priceAlignment="flex-end" />
      )}

      {insurance > 0 && (
        <PricingRow
          label={t('label.additional_coverage', { context: brand })}
          price={insurance}
          priceAlignment="flex-end"
        />
      )}

      {carbon > 0 && (
        <PricingRow
          label={t('label.carbon_offset', { context: brand })}
          price={carbon}
          priceAlignment="flex-end"
        />
      )}

      {installmentsFee > 0 && (
        <PricingRow
          label={t('label.deferred_payment_fee')}
          price={installmentsFee}
          priceAlignment="flex-end"
        />
      )}
    </>
  );
};

PurchasePricingExtras.propTypes = {
  carbon: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  installmentsFee: PropTypes.number.isRequired,
  insurance: PropTypes.number.isRequired,
  markup: PropTypes.number.isRequired,
  serviceLevel: PropTypes.shape({
    name: PropTypes.string,
    total: PropTypes.number,
  }),
  exchangeSurcharges: PropTypes.shape({
    items: PropTypes.arrayOf(
      PropTypes.shape({
        amount: PropTypes.number,
        message: PropTypes.string,
        type: PropTypes.string,
      }),
    ),
    total: PropTypes.number,
  }),
};

PurchasePricingExtras.defaultProps = {
  serviceLevel: {
    name: 'basic',
    total: 0,
  },
  exchangeSurcharges: null,
};

export default PurchasePricingExtras;
